import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { REQ_KINDS, ReqStatus, DueLeft } from "@/components/privacy/privacyConst";
import { RequestDialog } from "@/components/privacy/RequestDialog";
import { RequestSheet } from "@/components/privacy/RequestSheet";
import { TemplatesDialog } from "@/components/privacy/TemplatesDialog";

export const RequestsTab = ({ initialId, onOpened }) => {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const load = useCallback(() => api.get("/privacy/requests").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initialId) { setOpenId(initialId); onOpened?.(); } }, [initialId]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = rows.filter((r) => r.days_left != null);
  return (
    <div data-testid="requests-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 border-l-4 border-sky-600 bg-white px-5 py-4">
        <div className="max-w-2xl text-sm">
          <div className="font-semibold" data-testid="requests-open-summary">{open.length} richieste da evadere{open.some((r) => r.days_left < 0) && <span className="ml-2 text-red-700">· {open.filter((r) => r.days_left < 0).length} scadute</span>}</div>
          <p className="mt-1 text-muted-foreground">Risposta entro 30 giorni dalla ricezione. Gli amministratori ricevono campanella ed email all'arrivo, a 7 e 2 giorni dalla scadenza e se scaduta.</p>
        </div>
        <div className="flex gap-2"><Button variant="outline" onClick={() => setTplOpen(true)} data-testid="templates-open-button">Modelli di risposta</Button>
        <Button onClick={() => setCreating(true)} data-testid="request-new-button"><Plus className="mr-1 h-4 w-4" />Nuova richiesta</Button></div>
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="requests-empty">Nessuna richiesta registrata.</p> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Stato</TableHead><TableHead>Tipo</TableHead><TableHead>Interessato</TableHead><TableHead>Ricevuta</TableHead><TableHead>Scadenza</TableHead><TableHead>Chiusa</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setOpenId(r.id)} data-testid={`request-row-${r.id}`}>
                  <TableCell><ReqStatus r={r} /></TableCell>
                  <TableCell className="text-sm">{REQ_KINDS[r.kind]}</TableCell>
                  <TableCell className="font-semibold">{r.employee_name || r.requester_name}{!r.employee_id && <span className="ml-2 text-xs font-normal text-muted-foreground">esterno</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(r.received_on)}</TableCell>
                  <TableCell>{r.days_left != null ? <DueLeft r={r} /> : <span className="font-mono text-xs text-muted-foreground">{fmtDate(r.due_on)}</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{r.closed_on ? fmtDate(r.closed_on) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RequestDialog open={creating} onClose={() => setCreating(false)} onSaved={(r) => { setCreating(false); load(); setOpenId(r.id); }} />
      <TemplatesDialog open={tplOpen} onClose={() => setTplOpen(false)} />
      <RequestSheet req={rows.find((r) => r.id === openId)} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
};
